/**
 * Visit Metadata Constants
 *
 * Central config for vitals, symptoms, and visit action definitions.
 * Mirrors backend definitions in app/schemas/visit_metadata.py
 *
 * IMPORTANT: Action roles are derived from PermissionContext's ROLE_PERMISSIONS.
 * Do NOT hardcode roles here — update ROLE_PERMISSIONS instead.
 */

import { getRolesWithPermission } from '../context/PermissionContext';
import { SPECIALIZATION_CATALOGUE } from './specializations';

// ── Payment Modes ─────────────────────────────────────────────────────
export const PAYMENT_MODES = [
  { id: 'UPI',  label: 'UPI',  icon: '📱' },
  { id: 'CASH', label: 'Cash', icon: '💵' },
  { id: 'CARD', label: 'Card', icon: '💳' },
];

// ── Meta Types ───────────────────────────────────────────────────────
export const META_TYPES = {
  NOTE: 'NOTE',
  VITAL: 'VITAL',
  SYMPTOM: 'SYMPTOM',
  PRESCRIPTION: 'PRESCRIPTION', // Phase 2
  REPORT: 'REPORT',             // Phase 2
  PROCEDURE_NOTE: 'PROCEDURE_NOTE', // Phase 1b — junior's during-procedure note
  PRESCRIPTION_IMAGE: 'PRESCRIPTION_IMAGE', // images attached to a digital Rx (custom theme)
};

// ── Visit Actions (rendered as buttons on each visit card) ───────────
// `roles` derived from ROLE_PERMISSIONS in PermissionContext.
// `permission` is the key used to check access centrally.
export const VISIT_ACTIONS = [
  {
    key: 'notes',
    label: 'Notes',
    icon: '📝',
    metaType: META_TYPES.NOTE,
    permission: 'ADD_NOTES',
    get roles() { return getRolesWithPermission('ADD_NOTES'); },
  },
  {
    key: 'vitals',
    label: 'Vitals',
    icon: '🌡️',
    metaType: META_TYPES.VITAL,
    permission: 'ADD_VITALS',
    get roles() { return getRolesWithPermission('ADD_VITALS'); },
  },
  {
    key: 'symptoms',
    label: 'Symptoms',
    icon: '🤒',
    metaType: META_TYPES.SYMPTOM,
    permission: 'ADD_SYMPTOMS',
    get roles() { return getRolesWithPermission('ADD_SYMPTOMS'); },
  },
  {
    key: 'prescription',
    label: 'Prescription',
    icon: '💊',
    metaType: META_TYPES.PRESCRIPTION,
    permission: 'ADD_PRESCRIPTION',
    get roles() { return getRolesWithPermission('ADD_PRESCRIPTION'); },
  },
  {
    key: 'report',
    label: 'Report',
    icon: '📄',
    metaType: META_TYPES.REPORT,
    permission: 'ADD_REPORT',
    get roles() { return getRolesWithPermission('ADD_REPORT'); },
    isFileUpload: true,
  },
  {
    key: 'procedures',
    label: 'Procedures',
    icon: '💉',
    permission: 'ADD_PROCEDURE',
    get roles() { return getRolesWithPermission('ADD_PROCEDURE'); },
  },
  {
    // Lab tab — doctor advises (ORDER_LAB), receptionist assigns a technician
    // (ASSIGN_LAB_ORDER); gated on VIEW_LAB_QUEUE so both see it and it's
    // stripped when the module is off. The panel branches by permission.
    key: 'lab',
    label: 'Lab',
    icon: '🧪',
    permission: 'VIEW_LAB_QUEUE',
    get roles() { return getRolesWithPermission('VIEW_LAB_QUEUE'); },
  },
];


// ── Vital Definitions ────────────────────────────────────────────────
// Key → { label, unit, min, max, step, defaultSelected, inputType }
// Units follow Indian clinical practice (°F for temp, kg for weight, cm for height)
export const VITAL_DEFINITIONS = {
  body_temperature: {
    label: 'Body Temperature',
    unit: '°F',
    min: 95,
    max: 108,
    step: 0.1,
    defaultSelected: true,
    inputType: 'decimal',
    placeholder: '98.6',
  },
  blood_pressure_systolic: {
    label: 'BP Systolic',
    unit: 'mmHg',
    min: 60,
    max: 260,
    step: 1,
    defaultSelected: true,
    inputType: 'integer',
    placeholder: '120',
  },
  blood_pressure_diastolic: {
    label: 'BP Diastolic',
    unit: 'mmHg',
    min: 30,
    max: 160,
    step: 1,
    defaultSelected: true,
    inputType: 'integer',
    placeholder: '80',
  },
  heart_rate: {
    label: 'Heart Rate',
    unit: 'bpm',
    min: 30,
    max: 220,
    step: 1,
    defaultSelected: true,
    inputType: 'integer',
    placeholder: '72',
  },
  respiratory_rate: {
    label: 'Respiratory Rate',
    unit: 'breaths/min',
    min: 8,
    max: 60,
    step: 1,
    defaultSelected: false,
    inputType: 'integer',
    placeholder: '16',
  },
  oxygen_saturation: {
    label: 'SpO2',
    unit: '%',
    min: 50,
    max: 100,
    step: 1,
    defaultSelected: false,
    inputType: 'integer',
    placeholder: '98',
  },
  weight: {
    label: 'Weight',
    unit: 'kg',
    min: 0.5,
    max: 300,
    step: 0.1,
    defaultSelected: false,
    inputType: 'decimal',
    placeholder: '65',
  },
  height: {
    label: 'Height',
    unit: 'cm',
    min: 30,
    max: 250,
    step: 1,
    defaultSelected: false,
    inputType: 'integer',
    placeholder: '165',
  },
};

// Default vitals shown on first load (keys)
export const DEFAULT_VITAL_KEYS = Object.entries(VITAL_DEFINITIONS)
  .filter(([, v]) => v.defaultSelected)
  .map(([k]) => k);

// Optional vitals that can be added
export const OPTIONAL_VITAL_KEYS = Object.entries(VITAL_DEFINITIONS)
  .filter(([, v]) => !v.defaultSelected)
  .map(([k]) => k);


// ── Symptoms by Specialization ───────────────────────────────────────

// One ENT list, shared by both ENT dropdown entries (D42/S18).
const ENT_SYMPTOMS = [
  'Throat pain', 'Ear pain', 'Sinus pain', 'Headache',
  'Nose block', 'Fever',
];

export const SYMPTOMS_BY_SPECIALIZATION = {
  GENERAL_PHYSICIAN: [
    'Fever', 'Constipation', 'Diarrhea', 'Vomiting', 'Stomach pain',
    'Back pain', 'Headache', 'Body pain', 'Cold & cough',
    'Mild chest pain', 'Burning urination', 'Skin rashes',
    'High BP', 'High sugar (early)', 'Fatigue',
  ],
  GASTROENTEROLOGIST: [
    'Constipation', 'Diarrhea', 'Chronic acidity', 'Severe stomach pain',
    'Bloating', 'Vomiting', 'Blood in stool', 'Liver issues',
    'Jaundice', 'Fever', 'Loss of appetite',
  ],
  PULMONOLOGIST: [
    'Chronic cough', 'Breathlessness', 'Wheezing', 'Chest tightness',
    'Fever', 'Chest infection', 'Persistent cold',
  ],
  UROLOGIST: [
    'Burning urination', 'Blood in urine', 'Kidney stone pain',
    'Frequent urination', 'Lower abdominal pain', 'Fever',
  ],
  ORTHOPEDIC: [
    'Back pain', 'Joint pain', 'Fracture', 'Swelling in joints',
    'Neck pain', 'Sports injury', 'Difficulty walking', 'Fever',
  ],
  DERMATOLOGIST: [
    'Skin rash', 'Itching', 'Acne', 'Hair fall',
    'Fungal infection', 'Nail infection', 'Fever',
  ],
  PEDIATRICIAN: [
    'Fever', 'Constipation in child', 'Diarrhea', 'Cough',
    'Vomiting', 'Rash', 'Stomach pain', 'Breathing difficulty',
  ],
  GYNECOLOGIST: [
    'Lower abdominal pain', 'Irregular periods', 'Heavy bleeding',
    'White discharge', 'PCOS', 'Pregnancy', 'Fever', 'Infertility',
  ],
  CARDIOLOGIST: [
    'Chest pain', 'Palpitations', 'High BP', 'Breathlessness',
    'Swelling in legs', 'Dizziness', 'Fatigue', 'Fever',
  ],
  NEUROLOGIST: [
    'Severe headache', 'Migraine', 'Seizures', 'Numbness',
    'Weakness', 'Memory loss', 'Dizziness', 'Fever',
  ],
  DIABETOLOGIST: [
    'High blood sugar', 'Frequent urination', 'Excess thirst',
    'Weight loss', 'Slow wound healing', 'Fatigue',
    'Recurrent infections', 'Constipation',
  ],
  ENDOCRINOLOGIST: [
    'Thyroid issues', 'Hormonal imbalance', 'PCOS', 'Diabetes',
    'Weight gain / loss', 'Constipation', 'Fatigue',
  ],
  NEPHROLOGIST: [
    'Swelling', 'High creatinine', 'Reduced urine output',
    'Blood in urine', 'High BP', 'Fever',
  ],
  PSYCHIATRIST: [
    'Anxiety', 'Depression', 'Sleep issues', 'Panic attacks',
    'Stress', 'Psychosomatic symptoms',
  ],
  GENERAL_SURGEON: [
    'Severe abdominal pain', 'Appendicitis', 'Hernia',
    'Gallbladder stone', 'Piles', 'Abscess', 'Fever',
  ],
  ANESTHESIOLOGIST: [
    'Pre-surgery assessment', 'Pain management', 'Chronic pain',
    'Post-operative pain', 'Nerve block', 'Sedation consultation',
    'Epidural consultation', 'ICU consultation',
  ],
  OPHTHALMOLOGIST: [
    'Blurred vision', 'Eye pain', 'Redness', 'Watering',
    'Headache (vision-related)', 'Fever',
  ],
  ENT: ENT_SYMPTOMS,
  // D42/S18 — `MS_ENT` is a separate dropdown entry but the SAME clinical
  // field: it points at the very same array (not a copy), so the two can never
  // drift apart. The group's heading still reads the doctor's own label.
  MS_ENT: ENT_SYMPTOMS,
  AYURVEDA: [
    'Fever', 'Constipation', 'Acidity', 'Back pain',
    'Joint pain', 'Stress', 'Skin problems', 'Diabetes', 'PCOS',
  ],
  HOMEOPATHY: [
    'Fever', 'Allergy', 'Cold & cough', 'Migraine',
    'Skin issues', 'Joint pain', 'Stress', 'Constipation',
  ],
  PHYSIOTHERAPIST: [
    'Back pain', 'Neck pain', 'Muscle stiffness',
    'Post-surgery rehab', 'Sports injury', 'Joint pain',
  ],
  SEXOLOGIST: [
    'Erectile dysfunction', 'Premature ejaculation', 'Low libido',
    'Sexual anxiety', 'Infertility concerns',
  ],
  FERTILITY_IVF: [
    'Unable to conceive', 'Recurrent miscarriage', 'Hormonal imbalance',
    'PCOS', 'Low sperm count',
  ],
  DIETICIAN: [
    'Constipation', 'Weight gain', 'Weight loss', 'Diabetes diet',
    'Thyroid diet', 'PCOS diet', 'High cholesterol',
  ],
  DENTIST: [
    'Toothache', 'Gum bleeding', 'Cavity', 'Tooth sensitivity',
    'Bad breath', 'Jaw pain', 'Swollen gums',
  ],
  ONCOLOGIST: [
    'Unexplained weight loss', 'Persistent fatigue', 'Lumps',
    'Chronic pain', 'Fever', 'Night sweats',
  ],
  PHYSICIAN_ASSISTANT: [
    'Fever', 'Cold & cough', 'Body pain', 'Headache',
    'Fatigue', 'Stomach pain', 'Skin rashes',
  ],
};

// Common symptoms shown first regardless of specialization
export const COMMON_SYMPTOMS = [
  'Fever', 'Headache', 'Body pain', 'Cold & cough',
  'Constipation', 'Vomiting', 'Fatigue', 'Back pain',
];

/**
 * D23 — the PRESCRIPTION PAD's "Common symptoms" group is two entries, not
 * eight: a static list nobody's practice matched was the reason the picker was
 * a hunt. `COMMON_SYMPTOMS` above is left alone — it still feeds the nurse /
 * reception `AddSymptoms` surface and the known-vs-custom classification.
 */
export const PAD_COMMON_SYMPTOMS = ['Fever', 'Cough'];

/**
 * Get symptom suggestions for a specialization.
 * Returns { common, specialized } arrays.
 */
export function getSymptomSuggestions(specialization) {
  const specUpper = (specialization || '').toUpperCase();
  const entry = SPECIALIZATION_CATALOGUE.find((e) => e.value === specUpper);
  const key = entry ? entry.symptomKey : specUpper;
  const specialized = (key && SYMPTOMS_BY_SPECIALIZATION[key]) || [];
  const specializedSet = new Set(specialized);
  const common = COMMON_SYMPTOMS.filter((s) => !specializedSet.has(s));
  return { common, specialized };
}


// ── Roles that can see ALL metadata (not just own) ───────────────────
// Derived from central ROLE_PERMISSIONS (VIEW_ALL_METADATA)
export const FULL_VISIBILITY_ROLES = getRolesWithPermission('VIEW_ALL_METADATA');
